import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {CustomPropertyValuesTable} from '@github-ui/custom-properties-editing'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import type {PropertyDefinition, Repository} from '@github-ui/custom-properties-types'
import {updateOrgPropertyValuesPath} from '@github-ui/paths'
import sudo from '@github-ui/sudo'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SearchIcon} from '@primer/octicons-react'
import {Box, Button, Text, TextInput} from '@primer/react'
import {useEffect, useMemo, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {ServerErrorFormBanner} from './Banners'

const debounceAnnouncement = debounce(announce, 300)

export function PropertiesEditingPage({
  editingRepos,
  onDismiss,
  editableProperties,
  definitions,
  onSuccess,
}: {
  editableProperties: string[]
  editingRepos: Repository[]
  onDismiss: () => void
  definitions: PropertyDefinition[]
  onSuccess: () => void
}) {
  const {propertyValuesMap, setPropertyValue, revertPropertyValue} = useEditCustomProperties(
    editingRepos.map(r => r.properties || {}),
    definitions,
  )
  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const [submitting, setSubmitting] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const org = useCurrentOrg()

  const onSubmit = async () => {
    setErrorMessage('')
    if (submitting) {
      return
    }

    if (Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    setSubmitting(true)

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      if (!(await sudo())) {
        setSubmitting(false)
        setErrorMessage('Unauthorized')
        return
      }

      const repoIds = editingRepos.map(repo => repo.id)
      try {
        const result = await verifiedFetchJSON(updateOrgPropertyValuesPath({org: org.login}), {
          method: 'POST',
          body: {
            repoIds,
            properties: changedProperties,
          },
        })
        if (result.ok) {
          onSuccess()
        } else {
          const {error} = await result.json()
          setErrorMessage(error)
        }
      } catch {
        setErrorMessage('Failed to update properties')
      }
    } else {
      onDismiss()
    }

    setSubmitting(false)
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  useEffect(() => {
    const resultsFound = filteredDefinitions.length
    if (resultsFound === 1) {
      debounceAnnouncement('1 result found')
    } else {
      debounceAnnouncement(`${resultsFound} results found`)
    }
  }, [filteredDefinitions])

  if (!editingRepos.length) {
    return null
  }

  const pageTitle =
    editingRepos.length === 1
      ? `Set properties on ${editingRepos[0]!.name}`
      : `Set properties on ${editingRepos.length} selected repositories`

  if (submitting) {
    return null
  }

  return (
    <form data-testid="properties-editing-page">
      <Box sx={{display: 'flex', flexDirection: 'column', background: 'none', gap: 2}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2}}>{pageTitle}</Box>
        <TextInput
          leadingVisual={SearchIcon}
          placeholder="Filter properties"
          aria-label="Filter properties"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
      </Box>

      <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
        {errorMessage && (
          <Box sx={{m: 2}}>
            <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
          </Box>
        )}
        {filteredDefinitions.length === 0 ? (
          <Text sx={{color: 'fg.muted'}}>No properties that match</Text>
        ) : (
          <CustomPropertyValuesTable
            definitions={filteredDefinitions}
            editableDefinitions={editableDefinitions}
            propertyValuesMap={propertyValuesMap}
            setPropertyValue={setPropertyValue}
            revertPropertyValue={revertPropertyValue}
            orgName={org.login}
          />
        )}
      </Box>

      <Box sx={{display: 'flex', gap: 2}}>
        <Button type="submit" onClick={onSubmit} variant="primary">
          Save
        </Button>
        <Button onClick={onDismiss} variant="default">
          Cancel
        </Button>
      </Box>
    </form>
  )
}

try{ PropertiesEditingPage.displayName ||= 'PropertiesEditingPage' } catch {}